import React, { useEffect, useRef, useState } from "react";
import fasting from "./fasting";
// import anxiety from "./anxiety";
// import addiction from "./addiction";
import exercises from "./exercises";
import sugar_fast from "./sugar_fast";
// import meditation from "./meditation";

// import { CATEGORIES, DISH_NAMES, ENCOURAGEMENTS } from "./categories";

import mixpanel from 'mixpanel-browser';

mixpanel.init("3afc29307485d230149b9a9b14025d2a", {  api_host: "https://api-eu.mixpanel.com", autocapture: false, track_pageview: true});

/**
 * Memory.Food — MemoryFoodScreen
 * - Hero + category tabs (Fasting, Anxiety, Sleep, Addiction)
 * - One card at a time; swipe right = "this is me now" (speak encouragement + reward)
 * - Swipe left = skip
 * - Shows last-swiped item so TTS matches what the user heard
 * - Minimal, modern layout intended to match your existing style
 *
 * Usage:
 *  - Place in src/screens/MemoryFoodScreen.jsx
 *  - Import and render in your App: <MemoryFoodScreen />
 *  - Requires browser speechSynthesis API (for TTS) and standard WebAudio (used lightly here)
 */

const MODULES = [fasting, exercises, sugar_fast];
// Map keyed by module.KEY for direct category lookups
export const MODULE_MAP = Object.fromEntries(
  MODULES.map(m => [m.KEY, m])
);


// Build unified maps
export const CATEGORIES = Object.fromEntries(
  MODULES.map(m => [
    m.KEY,
    {
      id: m.KEY,
      title: m.TITLE,
      subtitle: m.SUBTITLE || "",
      sensations: m.SENSATIONS,
      mainword: m.MAINWORD || "",
      mainwords: m.MAINWORDS,
      min_online: m.MIN_ONLINE || 3,
      max_online: m.MAX_ONLINE || 15,
    }
  ])
);

export const DISH_NAMES = Object.assign(
  {},
  ...MODULES.map(m => m.DISH_NAMES)
);

export const ENCOURAGEMENTS = Object.assign(
  {},
  ...MODULES.map(m => m.ENCOURAGEMENTS)
);

const REACT_BEGIN = [
  "Mmm...",
  "Woo...",
  "Oh my...",
  // "Mmm...",
  // "Goodness...",
  "Oh yes! ...",
  "Nice...",
  "Damn...",
  "Sheeeesh...",
  "Whoa...",
  "Cool...",
  "Great! ...",
  "Welldone!...",
];


/* ---------------------------
   Small audio helper (WebAudio + TTS)
   --------------------------- */
function useAudio() {
  const ctxRef = useRef(null);

  useEffect(() => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      ctxRef.current = new AudioContext();
    } catch (e) {
      ctxRef.current = null;
    }
    return () => {
      if (ctxRef.current) try { ctxRef.current.close(); } catch (e) {}
    };
  }, []);

  function woosh(dir = "right") {
    const ctx = ctxRef.current;
    if (!ctx) return;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sine";
    o.frequency.value = dir === "right" ? 600 : 300;
    g.gain.value = 0.02;
    o.connect(g); g.connect(ctx.destination);
    o.start(); g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.07);
    o.stop(ctx.currentTime + 0.08);
  }

  function reward() {
    const ctx = ctxRef.current;
    if (!ctx) return;
    const o1 = ctx.createOscillator();
    const g1 = ctx.createGain();
    o1.type = "triangle";
    o1.frequency.value = 880;
    g1.gain.value = 0.03;
    o1.connect(g1); g1.connect(ctx.destination);
    o1.start(); g1.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.14);
    o1.stop(ctx.currentTime + 0.15);
  }

  return { woosh, reward };
}

/* ---------------------------
   Helpers
   --------------------------- */
function shuffleArray(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function scrambleText(s) {
  // Slightly scramble words but keep readable
  return s
    .split(" ")
    .map((word) => {
      if (word.length <= 3) return word;
      const mid = word.slice(1, -1).split("");
      // Scramble only 1 or 2 letters max
      const scrambleCount = Math.min(1, mid.length);
      // const scrambleCount = Math.max(1, Math.floor(mid.length / 2)); // shuffle fewer letters
      for (let i = 0; i < scrambleCount; i++) {
        const idx1 = Math.floor(Math.random() * mid.length);
        const idx2 = Math.floor(Math.random() * mid.length);
        [mid[idx1], mid[idx2]] = [mid[idx2], mid[idx1]];
      }
      return word[0] + mid.join("") + word[word.length - 1];
    })
    .join(" ");
}


/* ---------------------------
   TTS: speak an encouragement for a sensation
   --------------------------- */
function speakEncouragement(sensation, categoryId = "fasting") {
  if (!window.speechSynthesis) return;
  const dish = getDishName(sensation, categoryId);
  const begin = REACT_BEGIN[Math.floor(Math.random() * REACT_BEGIN.length)];
  const encourage = getEncouragement(sensation, categoryId) || "This is normal — you're doing well.";
  const line = `${begin}, ${dish}. ${encourage}`;
  const u = new SpeechSynthesisUtterance(line);
  u.rate = 0.90;
  u.pitch = 1.0;
  u.volume = 1.0;
  u.lang = "en-US";
  window.speechSynthesis.cancel();
  window.speechSynthesis.speak(u);
}


// category-aware getters: prefer per-module mapping, then fallback to merged global map
function getEncouragement(sensation, categoryId) {
  if (!sensation) return "";
  const mod = MODULE_MAP[categoryId];
  if (mod && mod.ENCOURAGEMENTS && mod.ENCOURAGEMENTS[sensation]) {
    return mod.ENCOURAGEMENTS[sensation];
  }
  return ENCOURAGEMENTS[sensation] || "";
}

function getDishName(sensation, categoryId) {
  if (!sensation) return sensation || "";
  const mod = MODULE_MAP[categoryId];
  if (mod && mod.DISH_NAMES && mod.DISH_NAMES[sensation]) {
    return mod.DISH_NAMES[sensation];
  }
  return DISH_NAMES[sensation] || sensation;
}


/* ---------------------------
   MemoryFoodScreen component
   --------------------------- */
export default function MemoryFoodScreen() {
  const { woosh, reward } = useAudio();

  const [categoryId, setCategoryId] = useState("fasting");
  
  const [queue, setQueue] = useState(() => shuffleArray(CATEGORIES["fasting"].sensations));
  const [current, setCurrent] = useState(queue[0] || null);
  const [history, setHistory] = useState([]);
  const [last, setLast] = useState(null);

  const [hungerLevel, setHungerLevel] = useState(0);
  const maxHunger = 10;
  const cat = CATEGORIES[categoryId];
  const [peopleOnline, setPeopleOnline] = useState(() => {
    return cat.min_online + Math.floor(Math.random() * (cat.max_online - cat.min_online + 1));
  });
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [pulse, setPulse] = useState(false);
  const pointerRef = useRef(null);
  const containerRef = useRef(null);

  const [drawerOpen, setDrawerOpen] = useState(false);

  const [lastSwipe, setLastSwipe] = useState(null);

  const completedTrackedRef = useRef(false);




  // for scramble animation re-render
  const [, tick] = useState(0);

  useEffect(() => {
    // small interval to update scramble effect
    const t = setInterval(() => tick((c) => c + 1), 800);
    return () => clearInterval(t);
  }, []);


  // update queue when category changes
  useEffect(() => {
    const arr = shuffleArray(CATEGORIES[categoryId].sensations);
    setQueue(arr);
    setCurrent(arr[0] || null);
    setLast(null);
    setHistory([]);           // clear history to avoid stale items
    setHungerLevel(0);
    setLastSwipe(null);
    completedTrackedRef.current = false;
  }, [categoryId]);

  // people online gentle random walk
  useEffect(() => {
    const t = setInterval(() => {
      setPeopleOnline((p) => {
        let next = p + (Math.floor(Math.random() * 5) - 2);
        if (next < cat.min_online) next = cat.min_online;
        if (next > cat.max_online) next = cat.max_online;
        return next;
      });
    }, 2200);
    return () => clearInterval(t);
  }, [cat.min_online, cat.max_online]);

  // pointer handlers
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    function onDown(e) {
      pointerRef.current = {
        startX: e.clientX || (e.touches && e.touches[0].clientX),
        startAt: performance.now(),
      };
    }
    function onUp(e) {
      if (!pointerRef.current) return;
      const endX = e.clientX || (e.changedTouches && e.changedTouches[0].clientX) || pointerRef.current.startX;
      const dx = endX - pointerRef.current.startX;
      const dt = performance.now() - pointerRef.current.startAt;
      const minSwipe = 20;
      if (Math.abs(dx) > minSwipe && dt < 1200) {
        handleSwipe(dx > 0 ? "right" : "left");
      }
      pointerRef.current = null;
    }

    el.addEventListener("pointerdown", onDown);
    el.addEventListener("pointerup", onUp);
    el.addEventListener("touchstart", onDown);
    el.addEventListener("touchend", onUp);

    return () => {
      el.removeEventListener("pointerdown", onDown);
      el.removeEventListener("pointerup", onUp);
      el.removeEventListener("touchstart", onDown);
      el.removeEventListener("touchend", onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, queue, categoryId, hungerLevel]);

  useEffect(() => {
    if (hungerLevel >= maxHunger && !completedTrackedRef.current) {
      mixpanel.track('completed', {
        sensation: last,
        hungerLevel
      });

      completedTrackedRef.current = true;
    }
  }, [hungerLevel, last, maxHunger]);



  function handleSwipe(direction) {
    // keep sound
    woosh(direction);

    // Right swipe: offer sensation to hunger -> increase meter
    if (direction === "right") {

      mixpanel.track('swiped right', {
          sensation: last,
          hungerLevel: hungerLevel + 1})

      setLastSwipe('right');
      // speak out loud
      if (current) speakEncouragement(current, categoryId);
      setHungerLevel((h) => Math.min(maxHunger, h + 1));
       // store last sensation before moving forward
      setLast(current);
      setPulse(true);
      setTimeout(() => setPulse(false), 300);
     
      } else {
        mixpanel.track('swiped left', {
          sensation: last,
          hungerLevel: hungerLevel + 1})

        setLastSwipe('left');
    }

   

    // / Animate card moving out
  setSwipeOffset(direction === "right" ? 200 : -200);
      setTimeout(() => {
        setSwipeOffset(0);
        setHistory((h) => [...h, current]);   // ← push current ONCE
        setLast(current);  
        // advanceCard(); // your function to go to next card

         // advance to next card
        setQueue((q) => {
          const nextQueue = q.slice(1);

          // Save current card to history BEFORE removing
          // setHistory(h => [...h, nextQueue[0]]);

           // store last card
          // setLast(nextQueue[0]); 
          // remove the current item (first)
          // nextQueue.shift();
          if (nextQueue.length === 0) {
            // reshuffle and refill if empty so user can continue swiping multiple times per day
            // const refill = shuffleArray([CATEGORIES[categoryId].sensations]);
            const refill = shuffleArray([...CATEGORIES[categoryId].sensations]);
            setCurrent(refill[0]);
            return refill;
          } else {
            setCurrent(nextQueue[0]);
            return nextQueue;
          }
        });

      }, 180);
  }

  function handleUndo() {
  setHistory((h) => {
    if (h.length === 0) return h;

    const newHistory = [...h];
    const previous = newHistory.pop();

    setQueue((q) => {
      return [previous, ...q.filter(x => x !== previous)];
    });

    setCurrent(previous);
    setLast(previous);
    setHungerLevel((lvl) => Math.max(0, lvl - 1));

    return newHistory;
  });
}




  function reset() {
    const arr = shuffleArray(CATEGORIES[categoryId].sensations);
    setQueue(arr);
    setCurrent(arr[0]);
    setLast(null);
    setHungerLevel(0);
    setLastSwipe(null);
    completedTrackedRef.current = false;
  }

  const hungerPct = Math.round((hungerLevel / maxHunger) * 100);

  /* -------------------------
     Implied daily completion: gentle message when full
     ------------------------- */
  const completed = hungerLevel >= maxHunger;
  // if (completed) {
  //   mixpanel.track('completed', {
  //     sensation: last,
  //     hungerLevel: hungerLevel +1
  //   });
  // }


  return (
    <div className="container py-2" style={{ maxWidth: 920, userSelect: "none" }}>
      {/* Hero */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <div style={{ fontWeight: 700, fontSize: 18 }}>Memory.Food</div>
          <div style={{ color: "#6b7280", fontSize: 12, textAlign: "left" }}>{cat.subtitle}</div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ fontSize: 13, color: "#9ca3af", display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ width: 10, height: 10, borderRadius: 20, background: "radial-gradient(circle at 30% 30%, #fff7c2, #00cd18ff)" }} />
            {peopleOnline} {cat.mainwords} online
          </div>
          {/* <button className="btn btn-outline-secondary btn-sm" onClick={reset}>Reset</button> */}
        
        {/* NEW: Drawer toggle button */}
        <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setDrawerOpen(true)}
        >
            ☰
        </button>
        </div>
      </div>


      {/* Main area */}
      <div className="row g-3">
        <div className="col-md-8">
          <div
            ref={containerRef}
            className="rounded d-flex align-items-center justify-content-center"
            style={{
              height: 420,
              background: "linear-gradient(180deg,#fbfdff,#ffffff)",
              borderRadius: 14,
              position: "relative",
              overflow: "hidden",
              boxShadow: "0 8px 28px rgba(15,23,42,0.06)",
              padding: 24,
            }}
          >
            {completed ? (
              <div className="text-center">
                <div style={{ fontSize: 22, fontWeight: 800 }}>Congratulations !!!</div>
                {/* <div style={{ fontSize: 22, fontWeight: 800 }}>Your will has been fed today.</div> */}
                <div style={{ marginTop: 10, color: "#6b7280" }}>You're been rewarded with strength of mind & body.</div>
                {/* <div style={{ fontSize: 14, marginTop: 12, color: "#6b7280" }}>Cheat Code: Drink Water.</div> */}
                <button className="btn btn-outline-primary mt-4" onClick={reset}>Restart ?</button>
                {/* <RandomTip /> */}

              </div>

              ) : (
              <div
                className="card p-4 text-center shadow"
                style={{
                  width: "100%",
                  maxWidth: 520,
                  borderRadius: 18,
                  background: "linear-gradient(180deg,#0f172a05,#ffffff)",
                  cursor: "grab",
                  userSelect: "none",
                  transform: `translateX(${swipeOffset}px)`,
                  transition: "transform 0.18s ease-out",
                }}
              >
                <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 18 }}>Swipe Right to Know What Your {cat.mainword} Is Telling You.</div>

                <div style={{ fontWeight: 800, fontSize: 24, letterSpacing: 0.6 }}>
                  {scrambleText(current)} ?
                </div>

                {/* <div style={{ fontSize: 13, color: "#9ca3af", marginTop: 10 }}>
                  Swipe right if you feel this now
                </div> */}

                <div style={{ marginTop: 18, display: "flex", justifyContent: "center", gap: 16 }}>
                  <div style={{ fontSize: 12, color: "#ef4444" }}>← Not Me Now</div>
                  <div style={{ fontSize: 12, color: "#10b981" }}>Me Right Now →</div>
                </div>

                {/* previous / last swiped (minimal) */}
                {lastSwipe == 'right' && (
                  <div style={{ marginTop: 10, textAlign: "center" }}>
                     {/* Modern Separator Line */}
                      <div style={{ 
                        marginTop: 0,
                        marginBottom: 10,
                        position: "relative",
                        textAlign: "center"
                      }}>
                        <span style={{
                          display: "inline-block",
                          padding: "0 10px",
                          background: "white",
                          fontSize: 11,
                          fontWeight: 600,
                          color: "#9ca3af",
                          position: "relative",
                          zIndex: 2
                        }}>
                          Previous
                        </span>

                        <div style={{
                          position: "absolute",
                          top: "50%",
                          left: 0,
                          right: 0,
                          height: 1,
                          background: "linear-gradient(to right, transparent, #e5e7eb, transparent)",
                          zIndex: 1
                        }} />
                      </div>

                      {/* Previous Section Content */}
                      <div style={{ 
                        fontSize: 12, 
                        color: "#6b7280", 
                        display: "flex", 
                        alignItems: "center", 
                        gap: 6,
                        justifyContent: "center",
                        paddingBottom: 4
                      }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="#6b7280">
                          <path d="M8 5v14l11-7z" />
                        </svg>

                        <span style={{ fontWeight: 600, color: "#374151" }}>
                          {last}
                        </span>

                        <span style={{ opacity: 0.9 }}>
                          {getEncouragement(last, categoryId) || ""}
                        </span>
                      </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* small touch area buttons for desktop testing */}
          {/* <div className="d-flex gap-2 justify-content-center mt-3">
            <button className="btn btn-outline-danger btn-sm" onClick={() => handleSwipe("left")}>Skip</button>
            <button className="btn btn-success btn-sm" onClick={() => handleSwipe("right")}>I feel this</button>
          </div> */}
        </div>

        {/* Right column: meter + how it works */}
        <div className="col-md-4">
          <div className="card p-3 shadow-sm" style={{ borderRadius: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Urge Satisfied ?</div>

            <div style={{ height: 12, width: "100%", background: "#eef2ff", borderRadius: 99, overflow: "hidden" }}>
              <div
                style={{
                  height: "100%",
                  width: `${hungerPct}%`,
                  background: "linear-gradient(90deg,#ffd166,#ffb3a7)",
                  transition: "width 320ms ease",
                }}
              />
            </div>

            <div style={{ marginTop: 10, fontSize: 12, color: "#6b7280" }}>{hungerLevel} / {maxHunger} sensations reframed </div>

            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>How it works</div>
              <div style={{ fontSize: 13, color: "#6b7280", marginTop: 6 }}>
                Swipe right if you're feeling the sensation listed. <b> Memory.Food will explain the true story and science behind the discomfort or uneasy sensation</b>, helping you reframe your relationship with these sensations.
              </div>
            </div>

            <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
              <button className="btn btn-outline-secondary btn-sm" onClick={handleUndo}>Undo</button>
              <button className="btn btn-primary btn-sm" onClick={() => setHungerLevel(maxHunger)}>Finish</button>
            </div>

           
          </div>

          {/* <div className="card p-3 mt-3" style={{ borderRadius: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700 }}>Categories</div>
            <div style={{ fontSize: 13, color: "#6b7280", marginTop: 8 }}>
              This tool helps you notice sensations and hear evidence-based explanations. It's for healthy adults learning to reframe urges.
            </div>
          </div> */}
        </div>
      </div>

      <style>{`
        .btn:focus { box-shadow: none !important; outline: none; }
      `}</style>

      {/* Right Drawer */}
        <div
        style={{
            position: "fixed",
            top: 0,
            right: drawerOpen ? 0 : "-260px",
            width: 260,
            height: "100vh",
            background: "#fff",
            boxShadow: "-2px 0 12px rgba(0,0,0,0.1)",
            transition: "right 0.25s ease",
            padding: 20,
            zIndex: 2000,
        }}
        >
        <div className="d-flex justify-content-between align-items-center mb-3">
            <div style={{ fontWeight: 600 }}>Categories</div>
            <button className="btn-close" onClick={() => setDrawerOpen(false)} />
        </div>

        <div className="d-flex flex-column gap-2">
            {Object.values(CATEGORIES).map((c) => (
            <button
                key={c.id}
                onClick={() => {
                setCategoryId(c.id);
                setDrawerOpen(false);
                }}
                className={`btn btn-sm ${categoryId === c.id ? "btn-dark" : "btn-outline-secondary"}`}
            >
                {c.title}
            </button>
            ))}
        </div>
        </div>

    </div>
    
  );
}
